/**
 * Stage-colored favicon — pure helper + DOM mutator.
 *
 * The browser tab favicon mirrors the in-app stage affordance
 * conventions (see `getStageLabel` in `@nu-assist/shared-fe` and
 * `StageBadge` in `@nu-assist/ui`):
 *
 *   - LOCAL → green favicon  (matches StageBadge `bg-green-500/...`)
 *   - DEV   → orange favicon (matches StageBadge `bg-orange-500/...`)
 *   - PROD  → the NU Advisory mark, green on transparent (a white
 *             tile behind it on dark browser chrome)
 *
 * Every stage shows the NU mark (2026-10-08); dev and local paint it
 * white on the stage colour. Each stage has an SVG (Chrome) and a 32 px
 * PNG (browsers without SVG favicons). Assets live in
 * `packages/web/public/`. `index.html` references the prod set as the
 * static default so a tab opened before `main.tsx` runs still shows the
 * brand icon (this is also what stays on prod — the swap is a no-op
 * there). The names are versioned (`.v2.`) because the web bucket serves
 * them `immutable` for a year and CloudFront keeps them as long: a new
 * icon needs a new name (see the comment in `index.html`). The `.ico` is prod-only: on dev and local it is REMOVED, so
 * every tab-icon candidate left is the stage colour (which candidate a
 * browser picks among several is its own heuristic; with one colour it
 * cannot pick the wrong one). The apple-touch icon is not a tab icon
 * (`rel="apple-touch-icon"`, a home-screen shortcut) and stays.
 *
 * 2026-10-27: a personal stage (`VITE_PERSONAL_STAGE`, e.g. `jl-dev`) swaps
 * the dev mark for its owner's initials, white on the same orange, as an
 * inline SVG (`personalFaviconSvg`), so each dev's tabs are told apart. The PNG
 * is dropped there too: the initials SVG must be the only tab-icon candidate.
 *
 * Keep the resolver `pure` so it stays testable without a DOM. The
 * DOM mutator (`applyStageFavicon`) is the only side-effecting half
 * and is what `main.tsx` calls before render.
 */

import type { Stage } from "@nu-assist/shared-fe";

export interface StageFavicon {
  svg: string;
  png: string;
}

const FAVICON_BY_STAGE: Record<Stage, StageFavicon> = {
  local: { svg: "/favicon-local.v2.svg", png: "/favicon-local-32.v2.png" },
  dev: { svg: "/favicon-dev.v2.svg", png: "/favicon-dev-32.v2.png" },
  prod: { svg: "/favicon-prod.v2.svg", png: "/favicon-32.v2.png" },
};

/**
 * Return the favicon hrefs for a given stage. Pure — no DOM, no env reads.
 * `index.html`'s static links are the same values this returns for
 * `stage === "prod"`, by design.
 */
export function getStageFavicon(stage: Stage): StageFavicon {
  return FAVICON_BY_STAGE[stage];
}

/** `jl-dev` → an inline SVG data URI: white `JL` on the dev orange tile. */
export function personalFaviconSvg(personalStage: string): string {
  const initials = personalStage.replace(/-dev$/, "").slice(0, 2).toUpperCase();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="6" fill="#f97316"/><text x="16" y="22.5" text-anchor="middle" font-family="Inter,Arial,sans-serif" font-size="17" font-weight="800" fill="#fff">${initials}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function iconLink(head: HTMLHeadElement, type: string): HTMLLinkElement {
  let link = head.querySelector<HTMLLinkElement>(`link[rel~="icon"][type="${type}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    link.type = type;
    head.appendChild(link);
  }
  return link;
}

/**
 * Set (or insert) the SVG and PNG `<link rel="icon">`s to the per-stage
 * favicons. Safe to call multiple times — it locates each link by its
 * `type` and mutates its `href`, otherwise it appends a fresh one to
 * `<head>`. Off prod, every other `rel="icon"` link (the prod `.ico`) is
 * removed.
 *
 * Idempotent and side-effect-only — no return value. Skips silently if
 * there is no `document` (e.g. SSR / Node test contexts without a DOM).
 */
export function applyStageFavicon(stage: Stage, personalStage?: string): void {
  if (typeof document === "undefined") return;

  const { svg, png } = getStageFavicon(stage);
  const head = document.head;
  if (!head) return;

  const svgLink = iconLink(head, "image/svg+xml");
  const pngLink = iconLink(head, "image/png");
  svgLink.href = svg;
  pngLink.href = png;
  if (stage === "prod") return;
  if (stage === "dev" && personalStage) {
    svgLink.href = personalFaviconSvg(personalStage);
    pngLink.remove();
  }
  // Token test by hand: `rel~="icon"` would also have to be trusted not to
  // match `apple-touch-icon`, and happy-dom's `~=` does match it.
  for (const link of head.querySelectorAll<HTMLLinkElement>("link[rel]")) {
    const isIcon = (link.getAttribute("rel") ?? "").split(/\s+/).includes("icon");
    if (isIcon && link !== svgLink && link !== pngLink) link.remove();
  }
}
